import { ReactNode, Suspense, lazy } from 'react';
import {
  Navigate,
  Outlet,
  useSearchParams,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
  AuthOnly,
  AnonymousOnly,
  OnboardingOnly,
  NotOnboardingOnly,
  LegacySessionProvider,
} from '@/components/sparrow-route/view-metrics-route';
import { APP_ROUTE } from '@/constants';
import { ProviderWrapper } from '@/providers/helpers/provider-wrapper';
import { LegacyAppProvider } from '@/app';
import { AppTopNavContainer } from '@/components/app-top-nav';
import ErrorBoundary, { NotFoundError } from '@/components/error-boundary';
import { EmbeddedHomeSkeleton } from './views/new-embedded/components/screen-skeletons';
import { EnterTransition } from './components/page-transition';
import { Toaster } from './components/ui/toaster';
import SocialAuth from './views/social-auth';
import ExternalAuth from './views/auth/onboarding/external-auth';
import NewVerifyEmailView from './views/auth/verify-email/new-verify-email';
import { Skeleton } from './components/ui/skeleton';
import { AppDashboardContentAreaSkeleton } from './components/AppDashboardSkeleton';

// Auth - /auth
const NewAuthLayout = lazy(() => import('./views/auth/new-auth-layout'));
const NewLoginView = lazy(
  () => import('@/views/auth/onboarding/login/new-login-view')
);
const NewSignUpViewWithLayout = lazy(() =>
  import('./views/auth/onboarding/new-onboarding-layout').then(module => ({
    default: module.NewSignUpViewWithLayout,
  }))
);
const NewResetPasswordView = lazy(
  () => import('./views/auth/reset-password/new-reset-password')
);
const NewResetSuccessView = lazy(
  () => import('./views/auth/reset-success/new-reset-success')
);
const NewUpdatePasswordView = lazy(
  () => import('./views/auth/update-password/new-update-password')
);

// Onboarding - /onboarding
const NewOnboardingLayout = lazy(() =>
  import('./views/auth/onboarding/new-onboarding-layout').then(module => ({
    default: module.NewOnboardingLayout,
  }))
);
const NewOnboardingView = lazy(() =>
  import('./views/auth/onboarding/new-onboarding-layout').then(module => ({
    default: module.NewOnboardingView,
  }))
);
const NewInviteView = lazy(() =>
  import('./views/auth/onboarding/invite/new-invite-screen').then(module => ({
    default: module.NewInviteView,
  }))
);

// Home - /home
const NewHomeLayout = lazy(() =>
  import('./views/home/new-home-layout').then(module => ({
    default: module.default,
  }))
);
const NewHomeLayoutContent = lazy(() =>
  import('./views/home/new-home-layout').then(module => ({
    default: module.NewHomeLayoutContent,
  }))
);
const NewHomeLayoutTopNav = lazy(() =>
  import('./views/home/new-home-layout').then(module => ({
    default: module.NewHomeLayoutTopNav,
  }))
);
const NewHomeView = lazy(() =>
  import('./views/home/new-home-view').then(module => ({
    default: module.default,
  }))
);
const NewHomeViewTopNav = lazy(() =>
  import('./views/home/new-home-view').then(module => ({
    default: module.NewHomeViewTopNav,
  }))
);

// Data Sources - /connections/data-sources
const NewDataSourceView = lazy(() =>
  import('./views/datasources/new-datasources-view').then(module => ({
    default: module.NewDataSourceView,
  }))
);
const NewDataSourcesViewTopNav = lazy(() =>
  import('./views/datasources/new-datasources-view').then(module => ({
    default: module.NewDataSourcesViewTopNav,
  }))
);
const NewAddDataSourceView = lazy(() =>
  import('./views/add-data-source/new-add-data-source-view').then(module => ({
    default: module.default,
  }))
);
const NewAddDataSourceViewTopNav = lazy(() =>
  import('./views/add-data-source/new-add-data-source-view').then(module => ({
    default: module.NewAddDataSourceViewTopNav,
  }))
);
const NewDatasourceDetailsTopNav = lazy(() =>
  import(
    './views/datasources/components/new-datasource-details'
  ).then(module => ({ default: module.NewDatasourceDetailsTopNav }))
);
const NewDatasourceDetailsView = lazy(() =>
  import(
    './views/datasources/components/new-datasource-details'
  ).then(module => ({ default: module.NewDatasourceDetailsView }))
);

// Destinations - /connections/destination
const NewDestinationView = lazy(() =>
  import(
    './views/destinations/destionation/new-destination-view'
  ).then(module => ({ default: module.default }))
);
const NewDestinationViewTopNav = lazy(() =>
  import(
    './views/destinations/destionation/new-destination-view'
  ).then(module => ({ default: module.NewDestinationViewTopNav }))
);

// Analytics - /analytics
const NewAnalyticsLayout = lazy(() =>
  import('./views/new-analytics/new-analytics-layout').then(module => ({
    default: module.NewAnalyticsLayout,
  }))
);
const NewAnalyticsLayoutTopNav = lazy(() =>
  import('./views/new-analytics/new-analytics-layout').then(module => ({
    default: module.NewAnalyticsLayoutTopNav,
  }))
);

// Social Media - /analytics/dashboard
const NewSocialMediaView = lazy(() =>
  import(
    './views/new-analytics/social-media/new-social-media-view'
  ).then(module => ({ default: module.NewSocialMediaView }))
);
const RecentPostTopNav = lazy(() =>
  import(
    './views/new-analytics/social-media/recent-post-view'
  ).then(module => ({ default: module.RecentPostTopNav }))
);
const RecentPostView = lazy(() =>
  import(
    './views/new-analytics/social-media/recent-post-view'
  ).then(module => ({ default: module.RecentPostView }))
);

// Email - /analytics/email
const NewEmailView = lazy(() =>
  import('./views/new-analytics/email/new-email-view').then(module => ({
    default: module.NewEmailView,
  }))
);
const EmailRecentCampaignTopNav = lazy(() =>
  import(
    './views/new-analytics/email/email-recent-campaign-view'
  ).then(module => ({ default: module.EmailRecentCampaignTopNav }))
);
const EmailRecentCampaignView = lazy(() =>
  import(
    './views/new-analytics/email/email-recent-campaign-view'
  ).then(module => ({ default: module.EmailRecentCampaignView }))
);

// Website - /analytics/website
const NewWebsiteView = lazy(() =>
  import('./views/new-analytics/website/new-website-view').then(module => ({
    default: module.NewWebsiteView,
  }))
);
const WebsitePagesTopNav = lazy(() =>
  import('./views/new-analytics/website/website-pages-view').then(module => ({
    default: module.WebsitePagesTopNav,
  }))
);
const WebsitePagesView = lazy(() =>
  import('./views/new-analytics/website/website-pages-view').then(module => ({
    default: module.WebsitePagesView,
  }))
);

// Ads - /analytics/ads
const NewAdsView = lazy(() =>
  import('./views/new-analytics/ads/new-ads-view').then(module => ({
    default: module.NewAdsView,
  }))
);
const AdsTopNav = lazy(() =>
  import('./views/new-analytics/ads/adsets-view').then(module => ({
    default: module.AdsTopNav,
  }))
);
const NewAdsDetailView = lazy(() =>
  import('./views/new-analytics/ads/adsets-view').then(module => ({
    default: module.AdsView,
  }))
);
const AdsetsView = lazy(() =>
  import('./views/new-analytics/ads/adsets-view').then(module => ({
    default: module.AdsetsView,
  }))
);
const AdsetsTopNav = lazy(() =>
  import('./views/new-analytics/ads/adsets-view').then(module => ({
    default: module.AdsetsTopNav,
  }))
);

// Embedded Charts - /embed/charts
const NewEmbeddedHomeContent = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewEmbeddedHomeContent,
  }))
);
const NewEmbeddedHomeTopNav = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewEmbeddedHomeTopNav,
  }))
);
const NewChartCreateEditScreen = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewChartCreateEditScreen,
  }))
);
const NewCreateChartTopNav = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewCreateChartTopNav,
  }))
);
const ChartCreateFormProvider = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.ChartCreateFormProvider,
  }))
);
const ChartDetailProvider = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.ChartDetailProvider,
  }))
);
const NewChartDetailTopNav = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewChartDetailTopNav,
  }))
);
const NewChartDetailScreen = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewChartDetailScreen,
  }))
);
const NewEditChartTopNav = lazy(() =>
  import('./views/new-embedded').then(module => ({
    default: module.NewEditChartTopNav,
  }))
);

// Reports - /reporting
const ReportLayout = lazy(() =>
  import('./views/reports/new-report-layout').then(module => ({
    default: module.ReportLayout,
  }))
);
const ReportViewTopNav = lazy(() =>
  import('./views/reports/new-report-layout').then(module => ({
    default: module.ReportViewTopNav,
  }))
);
const ReportDetailsComponent = lazy(
  () => import('./views/reports/report-details/new-report-details')
);
const ReportsList = lazy(
  () => import('./views/reports/components/reports/report-listing')
);
const CreatePage = lazy(() => import('./views/reports/create-flow'));

// Templates - /reporting/templates
const Templates = lazy(
  () => import('./views/reports/templates/template-listing')
);
const TemplateDetails = lazy(
  () => import('./views/reports/template-details/new-template-details')
);
const TemplateSettings = lazy(
  () => import('./views/reports/template-settings')
);

// Settings - /settings
const NewSettingsLayout = lazy(() =>
  import('./views/settings/new-settings-layout').then(module => ({
    default: module.NewSettingsLayout,
  }))
);
const SettingsTopNav = lazy(() =>
  import('./views/settings/new-settings-layout').then(module => ({
    default: module.SettingsTopNav,
  }))
);
const ProfileView = lazy(
  () => import('./views/settings/profile/new-profile-view')
);
const AccountView = lazy(
  () => import('./views/settings/account/new-account-view')
);
const ClientView = lazy(
  () => import('./views/settings/client/new-client-view')
);
const TeamSettings = lazy(() => import('./views/settings/team/new-team-view'));
const BillingRoute = lazy(() => import('./views/settings/billing-route'));

// Add-on - /addon
const AddonLayout = lazy(() => import('./views/add-on/new-index'));
const NewAddonSignup = lazy(
  () => import('./views/auth/onboarding/signup/new-addon-signup')
);

const AnonymousOnlyWrapper = ({ children }: { children: ReactNode }) => (
  <AnonymousOnly>
    <ProviderWrapper>
      <LegacyAppProvider>{children}</LegacyAppProvider>
    </ProviderWrapper>
  </AnonymousOnly>
);

/**
 * If the existing user (not a newly registered user) is invited to a workspace
 *
 * Then bruteforce the login again to accept the invitation
 *
 * TODO: Handle the case where the user is already logged in and the email is matched, don't have to login again, just need to accept the invitation
 */
const InvitationFlowWrapper = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();

  const isInvitingFlow =
    searchParams.has('email') &&
    searchParams.has('accessId') &&
    searchParams.has('businessId');

  if (isInvitingFlow) {
    return (
      <ProviderWrapper>
        <>{children}</>
      </ProviderWrapper>
    );
  }

  return <AnonymousOnlyWrapper>{children}</AnonymousOnlyWrapper>;
};

// Add new ProtectedRoute wrapper component
// const ProtectedRoute = ({ children }: { children: ReactNode }) => {
//   return (
//     <AuthOnly>
//       <OnboardingOnly>{children}</OnboardingOnly>
//     </AuthOnly>
//   );
// };

// Add this new component near the top of the file, after imports
const RootRedirect = () => {
  const [searchParams] = useSearchParams();
  const [cookies] = useCookies(['accessToken']);
  const location = useLocation();

  // Check if this is an invitation flow
  const isInvitingFlow =
    searchParams.has('email') &&
    searchParams.has('accessId') &&
    searchParams.has('businessId');

  // If authenticated, try to get workspace from localStorage
  const workspace = JSON.parse(localStorage.getItem('workspace') || '{}');
  const clientId = workspace?.client;

  const isAddonRoute = location.pathname.includes(`/${APP_ROUTE.ADDON}`);

  // If it's an addon route, don't redirect
  if (isAddonRoute) {
    // If no access token, redirect to addon login
    if (!cookies.accessToken) {
      return <Navigate to={`/${APP_ROUTE.ADDON}/${APP_ROUTE.LOGIN}`} replace />;
    }
    return null;
  }

  // If it's an invitation flow, allow access to continue
  if (isInvitingFlow) {
    return null;
  }

  // If no access token, redirect to login
  if (!cookies.accessToken) {
    return <Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`} replace />;
  }

  // If we have both access token and clientId, redirect to workspace
  if (clientId && !isAddonRoute) {
    return <Navigate to={`/ws/${clientId}/${APP_ROUTE.HOME}`} replace />;
  }

  // If we have token but no workspace, still redirect to login to be safe
  return <Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`} replace />;
};

const V2 = () => {
  const location = useLocation();
  return (
    <div className="App">
      <ErrorBoundary>
        <Routes>
          {/* Add root redirect route at the top */}
          <Route path="/" element={<RootRedirect />} />

          {/* EXTERNAL AUTH */}
          <Route
            path={`/${APP_ROUTE.EXTERNAL_AUTH}`}
            element={<ExternalAuth location={window.location} />}
          />

          {/* ADDON ROUTES - Login only */}
          <Route
            path={`/${APP_ROUTE.ADDON}`}
            element={
              <ProviderWrapper>
                <LegacyAppProvider>
                  <AddonLayout />
                </LegacyAppProvider>
              </ProviderWrapper>
            }
          >
            <Route
              path={`/${APP_ROUTE.ADDON}/${APP_ROUTE.LOGIN}`}
              element={
                <AnonymousOnly>
                  <div className="p-32">
                    <NewLoginView />
                  </div>
                </AnonymousOnly>
              }
            />
            <Route
              path={`/${APP_ROUTE.ADDON}/${APP_ROUTE.SIGN_UP}`}
              element={
                <AnonymousOnly>
                  <NewAddonSignup />
                </AnonymousOnly>
              }
            />
          </Route>

          {/* SOCIAL AUTH */}
          <Route
            path={`/${APP_ROUTE.SOCIAL_AUTH}`}
            element={
              <ProviderWrapper>
                <LegacyAppProvider>
                  <SocialAuth />
                </LegacyAppProvider>
              </ProviderWrapper>
            }
          />

          {/* VERIFY EMAIL */}
          <Route
            path={`/${APP_ROUTE.VERIFY_EMAIL}`}
            element={
              <ProviderWrapper>
                <LegacyAppProvider>
                  <NewVerifyEmailView />
                </LegacyAppProvider>
              </ProviderWrapper>
            }
          />

          {/* REDIRECTS */}
          <Route
            path={`/${APP_ROUTE.SIGN_UP}`}
            element={
              <Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}`} />
            }
          />

          <Route
            path={`/${APP_ROUTE.LOGIN}`}
            element={<Navigate to={`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`} />}
          />

          {/* SIGN UP */}
          <Route
            path={`/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}`}
            element={
              <AnonymousOnly>
                <ProviderWrapper>
                  <LegacyAppProvider>
                    <NewSignUpViewWithLayout />
                  </LegacyAppProvider>
                </ProviderWrapper>
              </AnonymousOnly>
            }
          />

          {/* FOR NEW USERS ONLY */}
          <Route
            path={`/${APP_ROUTE.AUTH}/${APP_ROUTE.SIGN_UP}/${APP_ROUTE.INVITED}`}
            element={
              <ErrorBoundary>
                <ProviderWrapper>
                  {/* <SilentLogout> */}
                  <>
                    <NewInviteView />
                    <Toaster />
                  </>
                  {/* </SilentLogout> */}
                </ProviderWrapper>
              </ErrorBoundary>
            }
          />

          {/* AUTH */}
          <Route path={`/${APP_ROUTE.AUTH}`} element={<NewAuthLayout />}>
            {/* EMAIL VERIFICATION */}
            <Route
              path={APP_ROUTE.VERIFICATION}
              element={
                <AnonymousOnlyWrapper>
                  <NewVerifyEmailView />
                </AnonymousOnlyWrapper>
              }
            />

            {/* FORGOT PASSWORD */}
            <Route
              path={`${APP_ROUTE.LOGIN}/${APP_ROUTE.FORGOT_PASSWORD}`}
              element={
                <AnonymousOnlyWrapper>
                  <NewResetPasswordView />
                </AnonymousOnlyWrapper>
              }
            />

            {/* FORGOT PASSWORD SUCCESS */}
            <Route
              path={`${APP_ROUTE.LOGIN}/${APP_ROUTE.FORGOT_PASSWORD}/success`}
              element={
                <AnonymousOnlyWrapper>
                  <NewResetSuccessView />
                </AnonymousOnlyWrapper>
              }
            />

            {/* UPDATE PASSWORD */}
            <Route
              path={`${APP_ROUTE.LOGIN}/${APP_ROUTE.UPDATE_PASSWORD}`}
              element={
                <AnonymousOnlyWrapper>
                  <NewUpdatePasswordView />
                </AnonymousOnlyWrapper>
              }
            />

            {/* LOGIN */}
            <Route
              path={APP_ROUTE.LOGIN}
              element={
                <InvitationFlowWrapper>
                  <NewLoginView />
                  <Toaster />
                </InvitationFlowWrapper>
              }
            />

            {/* LOGIN INDEX */}
            <Route index element={<Navigate to={APP_ROUTE.LOGIN} />} />
          </Route>

          {/* ONBOARDING */}
          <Route
            path={`/${APP_ROUTE.ONBOARDING}`}
            element={
              <AuthOnly>
                <NotOnboardingOnly>
                  <NewOnboardingLayout />
                </NotOnboardingOnly>
              </AuthOnly>
            }
          >
            {/* Index route */}
            <Route index element={<NewOnboardingView />} />

            {/* Catch all other paths and redirect to index */}
            <Route
              path="*"
              element={<Navigate to={`/${APP_ROUTE.ONBOARDING}`} replace />}
            />
          </Route>

          {/* HOME */}
          <Route
            path="/ws/:clientId"
            element={
              <AuthOnly>
                <OnboardingOnly>
                  <NewHomeLayout />
                </OnboardingOnly>
              </AuthOnly>
            }
          >
            {/* Addon routes */}
            <Route
              path={`${APP_ROUTE.ADDON}/${APP_ROUTE.TEMPLATE}/:id`}
              element={
                <AuthOnly>
                  <ProviderWrapper>
                    <LegacySessionProvider>
                      <TemplateSettings />
                    </LegacySessionProvider>
                  </ProviderWrapper>
                </AuthOnly>
              }
            />

            <Route
              path={`${APP_ROUTE.ADDON}/${APP_ROUTE.DATA_SOURCES}/${APP_ROUTE.ADD}`}
              element={
                <AuthOnly>
                  <ProviderWrapper>
                    <LegacySessionProvider>
                      <NewAddDataSourceView />
                    </LegacySessionProvider>
                  </ProviderWrapper>
                </AuthOnly>
              }
            />

            {/* DASHBOARD */}
            <Route
              path={APP_ROUTE.HOME}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewHomeViewTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <NewHomeView />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            {/* CONNECTIONS */}
            <Route
              path={`${APP_ROUTE.CONNECTIONS}`}
              element={<Navigate to={APP_ROUTE.DATA_SOURCES} />}
            />

            {/* CONNECTIONS - DATA SOURCES */}
            <Route
              path={`${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewDataSourcesViewTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewDataSourceView />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            {/* CONNECTIONS - DATA SOURCES - ADD */}
            <Route
              path={`${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}/${APP_ROUTE.ADD}`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewAddDataSourceViewTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewAddDataSourceView />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            {/* CONNECTIONS - DATA SOURCES - DETAILS */}
            <Route
              path={`${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}/:datasourceId`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewDatasourceDetailsTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewDatasourceDetailsView />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            {/* CONNECTIONS - DESTINATIONS */}
            <Route
              path={`${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DESTINATION}`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewDestinationViewTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewDestinationView />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            {/* ANALYTICS */}
            <Route
              path={APP_ROUTE.ANALYTICS}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewAnalyticsLayoutTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewAnalyticsLayout />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </Suspense>
              }
            >
              <Route
                path={APP_ROUTE.DASHBOARD}
                element={<NewSocialMediaView />}
              />

              <Route path={APP_ROUTE.ADS} element={<NewAdsView />} />

              <Route path={APP_ROUTE.EMAIL} element={<NewEmailView />} />

              <Route path={APP_ROUTE.WEBSITE} element={<NewWebsiteView />} />

              <Route
                index
                element={<Navigate to={`${APP_ROUTE.DASHBOARD}`} replace />}
              />
            </Route>

            {/* Fix absolute paths to relative paths */}
            <Route
              path={`${APP_ROUTE.ANALYTICS}/${APP_ROUTE.DASHBOARD}/${APP_ROUTE.POSTS}`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <RecentPostTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <RecentPostView />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            <Route
              path={`${APP_ROUTE.ANALYTICS}/${APP_ROUTE.EMAIL}/${APP_ROUTE.POSTS}`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <EmailRecentCampaignTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <EmailRecentCampaignView />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            <Route
              path={`${APP_ROUTE.ANALYTICS}/${APP_ROUTE.WEBSITE}/:tab`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <WebsitePagesTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <WebsitePagesView />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            <Route
              path={`${APP_ROUTE.ANALYTICS}/${APP_ROUTE.ADS}/:accountId/:campaignId/${APP_ROUTE.ADSETS}`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <AdsetsTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <AdsetsView />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            <Route
              path={`${APP_ROUTE.ANALYTICS}/${APP_ROUTE.ADS}/:accountId/:campaignId/${APP_ROUTE.ADSETS}/:adsetId`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <AdsTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <NewAdsDetailView />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            {/* EMBEDDED CHARTS with clientId */}
            <Route
              path={`${APP_ROUTE.EMBED}/${APP_ROUTE.CHARTS}`}
              element={
                <Suspense fallback={<EmbeddedHomeSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <NewEmbeddedHomeTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <NewEmbeddedHomeContent />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            />

            <Route
              path={`${APP_ROUTE.EMBED}/${APP_ROUTE.CHARTS}/create`}
              element={
                <ChartCreateFormProvider>
                  <AppTopNavContainer>
                    <NewCreateChartTopNav />
                  </AppTopNavContainer>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewChartCreateEditScreen />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </ChartCreateFormProvider>
              }
            />

            <Route
              path={`${APP_ROUTE.EMBED}/${APP_ROUTE.CHARTS}/:chartId`}
              element={
                <ErrorBoundary>
                  <NewHomeLayoutTopNav>
                    <NewChartDetailTopNav />
                  </NewHomeLayoutTopNav>
                  <ChartDetailProvider>
                    <NewHomeLayoutContent>
                      <EnterTransition key={location.pathname}>
                        <NewChartDetailScreen />
                      </EnterTransition>
                    </NewHomeLayoutContent>
                  </ChartDetailProvider>
                </ErrorBoundary>
              }
            />

            <Route
              path={`${APP_ROUTE.EMBED}/${APP_ROUTE.CHARTS}/:chartId/edit`}
              element={
                <ChartDetailProvider>
                  <AppTopNavContainer>
                    <NewEditChartTopNav />
                  </AppTopNavContainer>
                  <NewHomeLayoutContent>
                    <EnterTransition key={location.pathname}>
                      <NewChartCreateEditScreen />
                    </EnterTransition>
                  </NewHomeLayoutContent>
                </ChartDetailProvider>
              }
            />

            {/* Reports */}
            <Route
              path="reporting/*"
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <ReportViewTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <ReportLayout />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            >
              {/* Reports section */}
              <Route path={`${APP_ROUTE.REPORTS}/*`} element={<Outlet />}>
                <Route path="" element={<ReportsList />} />
                <Route path=":id" element={<ReportDetailsComponent />} />
                <Route path="create" element={<CreatePage />} />
              </Route>

              {/* Templates section */}
              <Route path={`${APP_ROUTE.TEMPLATES}/*`} element={<Outlet />}>
                <Route path="" element={<Templates />} />
                <Route path=":id" element={<TemplateDetails />} />
                {/* Template settings */}
                <Route path=":id/settings" element={<TemplateSettings />} />
              </Route>
            </Route>

            {/* SETTINGS */}
            <Route
              path={`${APP_ROUTE.SETTINGS}/*`}
              element={
                <Suspense fallback={<AppDashboardContentAreaSkeleton />}>
                  <NewHomeLayoutTopNav>
                    <SettingsTopNav />
                  </NewHomeLayoutTopNav>
                  <NewHomeLayoutContent>
                    <NewSettingsLayout />
                  </NewHomeLayoutContent>
                </Suspense>
              }
            >
              <Route path={`${APP_ROUTE.PROFILE}`} element={<ProfileView />} />
              <Route path={`${APP_ROUTE.ACCOUNT}`} element={<AccountView />} />
              <Route path={`${APP_ROUTE.WORKSPACE}`} element={<ClientView />} />
              <Route path={`${APP_ROUTE.TEAM}`} element={<TeamSettings />} />
              <Route path={`${APP_ROUTE.BILLING}`} element={<BillingRoute />} />
            </Route>
          </Route>

          {/* 404 Route - Must be last */}
          <Route path="*" element={<NotFoundError />} />
        </Routes>
      </ErrorBoundary>
    </div>
  );
};

export default V2;
